import { useState } from "react";
import { useNavigate } from "react-router";

import { Alert, Box, Heading, HStack, Stack, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { Select } from "chakra-react-select";

import { BindingsImportRowType } from "@/codegen/openapi2";
import { getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetQueryKey } from "@/codegen/openapi2/@tanstack/react-query.gen";
import { Button } from "@/codegen/ui/button";
import { Field } from "@/codegen/ui/field";
import { FileUploadDropzone, FileUploadList, FileUploadRoot } from "@/codegen/ui/file-button";
import { toaster } from "@/codegen/ui/toaster";
import { TableLayout } from "@/components/2/layouts/table";
import { queryClient } from "@/services/2/react-query";
import {
  useApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPost,
  useGetAgenciesGeographiesAgenciesGet,
  useGetLegalEntityLegalEntitiesGet,
  usePreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPost,
} from "@/services/2/react-query/hooks";

import { CodeCell, RequestedTypeCell, StatusCell } from "./cells";
import { FormatReminder } from "./format-reminder";
import { ImportValidation } from "./import-validation";
import { PreviewCounts } from "./preview-counts";
import { AgencyOptionType, TestedImportType } from "./types";

// A refused test or validation comes as `{ detail: [{ msg, type }] }`.
const getErrorDetail = (error: unknown): { msg: string; type: string } | null => {
  if (
    error &&
    typeof error === "object" &&
    "detail" in error &&
    Array.isArray(error.detail) &&
    typeof error.detail[0]?.msg === "string" &&
    typeof error.detail[0]?.type === "string"
  ) {
    return error.detail[0];
  }
  return null;
};

const getFileErrorMessage = (error: unknown): string =>
  getErrorDetail(error)?.msg ||
  "Le test du fichier a échoué. Réessayez, ou contactez la DSI si l'erreur persiste.";

const getApplyErrorMessage = (error: unknown): string =>
  getErrorDetail(error)?.msg ||
  "La validation de l'import a échoué. Réessayez, ou contactez la DSI si l'erreur persiste.";

const columnHelper = createColumnHelper<BindingsImportRowType>();

const columns = [
  columnHelper.accessor("postal_code", {
    header: "Code postal",
    cell: ({ row }) => (
      <CodeCell code={row.original.postal_code} padded={row.original.postal_code_padded} />
    ),
    size: 140,
  }),
  columnHelper.accessor("insee_code", {
    header: "Code INSEE",
    cell: ({ row }) => (
      <CodeCell code={row.original.insee_code} padded={row.original.insee_code_padded} />
    ),
    size: 140,
  }),
  columnHelper.accessor("city_name", {
    header: "Commune",
    cell: ({ cell }) => cell.getValue() || "-",
    size: 220,
  }),
  columnHelper.accessor("requested_type", {
    header: "Type de zone demandé",
    cell: ({ cell }) => <RequestedTypeCell requestedType={cell.getValue()} />,
    size: 180,
  }),
  columnHelper.accessor("status", {
    header: "Statut",
    cell: ({ row }) => <StatusCell row={row.original} />,
    size: 260,
  }),
  columnHelper.accessor("line_numbers", {
    header: "Lignes du fichier",
    cell: ({ cell }) => cell.getValue().join(", "),
    size: 140,
  }),
];

export const PostalCodesImport = () => {
  const navigate = useNavigate();
  const [agencyUuid, setAgencyUuid] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isFileRejected, setIsFileRejected] = useState(false);
  const [testedImport, setTestedImport] = useState<TestedImportType | null>(null);
  const [areTransfersConfirmed, setAreTransfersConfirmed] = useState(false);
  const [isPreviewStale, setIsPreviewStale] = useState(false);

  const {
    mutate: previewImport,
    error: previewError,
    isPending: isPreviewPending,
    reset: resetPreview,
  } = usePreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPost();
  const {
    mutate: applyImport,
    error: applyError,
    isPending: isApplyPending,
    reset: resetApply,
  } = useApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPost();
  const isPending = isPreviewPending || isApplyPending;

  const { data: legalEntities } = useGetLegalEntityLegalEntitiesGet({}, [], {
    staleTime: 12 * 60 * 60 * 1000, // 12 hours
  });
  const { data: agencies } = useGetAgenciesGeographiesAgenciesGet({}, [], {
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const agencyOptions = (legalEntities || [])
    .map(legalEntity => ({
      label: legalEntity.name,
      options: (agencies || [])
        .filter(agency => agency.legal_entity_uuid === legalEntity.uuid)
        .map(agency => ({
          value: agency.uuid,
          label: agency.name,
          legalEntityName: legalEntity.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .filter(group => group.options.length > 0);
  const selectedAgencyOption =
    agencyOptions.flatMap(group => group.options).find(option => option.value === agencyUuid) ||
    null;

  const clearPreview = () => {
    setTestedImport(null);
    setAreTransfersConfirmed(false);
    setIsPreviewStale(false);
    resetPreview();
    resetApply();
  };

  const runPreview = (testedAgencyUuid: string, testedFile: File) =>
    previewImport(
      { path: { agency_uuid: testedAgencyUuid }, body: { file: testedFile } },
      { onSuccess: preview => setTestedImport({ file: testedFile, preview }) }
    );

  const testFile = () => {
    if (!agencyUuid || !file) return;
    clearPreview();
    runPreview(agencyUuid, file);
  };

  const validateImport = () => {
    if (!agencyUuid || !testedImport) return;
    applyImport(
      {
        path: { agency_uuid: agencyUuid },
        body: { file: testedImport.file, fingerprint: testedImport.preview.fingerprint },
      },
      {
        onSuccess: () => {
          // Drop the cached list, so the postal codes page shows the new bindings at once.
          queryClient.removeQueries({
            queryKey: getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetQueryKey(),
          });
          toaster.create({
            title: "L'import a bien été validé.",
            description: selectedAgencyOption
              ? `Les codes postaux de l'agence ${selectedAgencyOption.label} (${selectedAgencyOption.legalEntityName}) sont à jour.`
              : undefined,
            type: "success",
          });
          void navigate("..", { relative: "path" });
        },
        onError: error => {
          // The bindings changed since the test: show the up-to-date preview,
          // and ask again for the transfers confirmation.
          if (getErrorDetail(error)?.type === "STALE_FINGERPRINT") {
            clearPreview();
            setIsPreviewStale(true);
            runPreview(agencyUuid, testedImport.file);
          }
        },
      }
    );
  };

  return (
    <Box>
      <Box p={8} backgroundColor={"gray.100"}>
        <HStack justify="space-between" align="center">
          <Heading size="2xl">Importer les codes postaux d'une agence</Heading>
          <Button variant="ghost" onClick={() => navigate("..", { relative: "path" })}>
            Retour aux codes postaux
          </Button>
        </HStack>
      </Box>

      <Stack p={8} gap={6}>
        <Field label="Agence" maxW="md">
          <Box w="full">
            <Select<AgencyOptionType>
              options={agencyOptions}
              value={selectedAgencyOption}
              onChange={option => {
                setAgencyUuid(option ? option.value : null);
                clearPreview();
              }}
              // Used by the search and the screen readers: homonym agencies differ by entity.
              getOptionLabel={option => `${option.label} (${option.legalEntityName})`}
              formatOptionLabel={option => (
                <HStack gap={2}>
                  <Text>{option.label}</Text>
                  <Text textStyle="xs" color="fg.muted">
                    {option.legalEntityName}
                  </Text>
                </HStack>
              )}
              placeholder="Choisir une agence"
              isDisabled={isPending}
            />
          </Box>
        </Field>

        <FormatReminder />

        <FileUploadRoot
          accept=".csv"
          maxW="xl"
          alignItems="stretch"
          disabled={isPending}
          onFileChange={({ acceptedFiles, rejectedFiles }) => {
            setIsFileRejected(rejectedFiles.length > 0);
            const acceptedFile = acceptedFiles[0] || null;
            if (acceptedFile !== file) {
              setFile(acceptedFile);
              clearPreview();
            }
          }}
        >
          <FileUploadDropzone
            label="Sélectionner un fichier ou le glisser-déposer ici"
            description="Fichier .csv uniquement"
          />
          <FileUploadList clearable showSize />
        </FileUploadRoot>
        {isFileRejected && <Text color="fg.error">Seul un fichier .csv peut être importé.</Text>}

        <Box>
          <Button
            onClick={testFile}
            disabled={!agencyUuid || !file || isApplyPending}
            loading={isPreviewPending}
          >
            Tester le fichier
          </Button>
        </Box>

        {isPreviewStale && (
          <Alert.Root status="warning">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Les données ont changé depuis le test</Alert.Title>
              <Alert.Description>
                Rien n'a été importé. L'aperçu ci-dessous a été mis à jour : vérifiez-le, puis
                validez à nouveau.
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        {previewError && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Le fichier ne peut pas être importé</Alert.Title>
              <Alert.Description>{getFileErrorMessage(previewError)}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        {testedImport && (
          <Stack gap={4}>
            <Heading size="lg">Changements prévus</Heading>
            {selectedAgencyOption && (
              <Text>
                Agence <b>{selectedAgencyOption.label}</b>, entité{" "}
                <b>{selectedAgencyOption.legalEntityName}</b>
              </Text>
            )}
            <PreviewCounts preview={testedImport.preview} />
            <TableLayout
              data={testedImport.preview.rows}
              columns={columns}
              withPagination
              withSorting={false}
            />
            <ImportValidation
              preview={testedImport.preview}
              areTransfersConfirmed={areTransfersConfirmed}
              setAreTransfersConfirmed={setAreTransfersConfirmed}
              onValidate={validateImport}
              isPending={isApplyPending}
            />
          </Stack>
        )}

        {applyError && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>L'import n'a pas été validé</Alert.Title>
              <Alert.Description>{getApplyErrorMessage(applyError)}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}
      </Stack>
    </Box>
  );
};
